import React, { useState, useEffect, useMemo } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/common/BreadCrumb";
import {
  DefaultButton,
  DefaultPrimaryButton,
  Input,
  InputWithLabel,
  SelectBox,
  SelectWithLabel,
} from "../../components/micro/input";
import { InputError } from "../../components/error/error";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Stack from "@mui/material/Stack";
import { useDispatch, useSelector } from "react-redux";
import { createTechnicians, getTechnician } from "../../redux/technician/slice";
import { getCountryData } from "../../redux/address/slice";
import { Eye, EyeSlash } from "../../assets/svg";
import { newPassword } from "../../redux/auth/slice";
import { Chip, IconButton } from "@mui/material";
import { Cancel } from "@mui/icons-material";

const CreateEditTechnicianAccount = () => {
  const [countryArray, setCountryArray] = useState([]);
  const [allTypeListArray, setAllTypeListArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const technicianData = location?.state?.technician;
  const isEditMode = location?.state?.mode;

  useEffect(() => {
    const mockRoles = [
      { id: 1, value: "electrician", label: "Electrician" },
      { id: 2, value: "gas", label: "Gas" },
      { id: 3, value: "exhaust", label: "Exhaust" },
      { id: 4, value: "plumbing", label: "Plumbing" },
      { id: 5, value: "electrician_plumbing", label: "Electrician & Plumbing" },
    ];

    setAllTypeListArray(mockRoles);
  }, []);

  //Selector
  const { technicianDetails, countryData, countryLoading } =
    useSelector((store) => ({
      //technicianDetails
      technicianDetails: store?.technicianData?.getTechnicianStatus?.data,
      //Contry Data
      countryData: store?.addressData?.getCountryDataStatus?.data,
      countryLoading: store?.addressData?.getCountryDataStatus?.loading,
    }));

  // get technician and country
  useEffect(() => {
    if (technicianData?.id) {
      dispatch(getTechnician(technicianData.id));
    }
    dispatch(getCountryData());
  }, [technicianData, dispatch]);

  useEffect(() => {
    if (countryData?.data) {
      let data = countryData?.data?.map((x) => ({
        ...x,
        label: x.id,
      }));
      setCountryArray(data);
    }
  }, [countryData]);

  const countryValueGet = {
    label: technicianDetails?.data?.country?.name,
    ...technicianDetails?.data?.country,
  };

  const initialValues = useMemo(
    () => ({
      fullName: technicianData?.id ? technicianData?.full_name : "",
      companyName: technicianData?.id ? technicianData?.company_name : "",
      email: technicianData?.id ? technicianData?.email : "",
      gender: technicianData?.id ? technicianData?.gender : "",
      phone: technicianData?.id ? technicianData?.phone : "",
      startTime: technicianData?.start_time
        ? dayjs(technicianData?.start_time, "HH:mm A")
        : dayjs()
            .set("hour", 9)
            .set("minute", 0)
            .set("second", 0)
            .set("millisecond", 0),

      endTime: technicianData?.end_time
        ? dayjs(technicianData?.end_time, "HH:mm A")
        : dayjs()
            .set("hour", 16)
            .set("minute", 0)
            .set("second", 0)
            .set("millisecond", 0),

      country: technicianData?.id
        ? technicianData?.country_id
          ? countryValueGet
          : ""
        : "",
      type:
        allTypeListArray.length > 0
          ? (() => {
              let technicianTypes = null;
              if (
                technicianDetails?.data?.types &&
                Array.isArray(technicianDetails.data.types)
              ) {
                technicianTypes = technicianDetails.data.types;
              }
              else if (
                technicianDetails?.types &&
                Array.isArray(technicianDetails.types)
              ) {
                technicianTypes = technicianDetails.types;
              }
              else if (
                technicianDetails?.data?.technician?.types &&
                Array.isArray(technicianDetails.data.technician.types)
              ) {
                technicianTypes = technicianDetails.data.technician.types;
              }
              else if (
                technicianDetails?.data?.technician_types &&
                Array.isArray(technicianDetails.data.technician_types)
              ) {
                technicianTypes = technicianDetails.data.technician_types;
              } else if (
                technicianDetails?.technician_types &&
                Array.isArray(technicianDetails.technician_types)
              ) {
                technicianTypes = technicianDetails.technician_types;
              }

              if (technicianTypes && technicianTypes.length > 0) {
                const mappedTypes = technicianTypes
                  .map((techType) => {
                    const typeValue =
                      typeof techType === "object" ? techType?.type : techType;
                    if (!typeValue) return null;

                    const role = allTypeListArray.find(
                      (role) => role.value === typeValue
                    );
                    return role || { label: typeValue, value: typeValue };
                  })
                  .filter(Boolean);

                if (mappedTypes.length > 0) {
                  return mappedTypes;
                }
              }

              if (
                technicianData?.types &&
                Array.isArray(technicianData.types)
              ) {
                const mappedTypes = technicianData.types
                  .map((typeValue) => {
                    if (!typeValue) return null;
                    const role = allTypeListArray.find(
                      (role) => role.value === typeValue
                    );
                    return role || { label: typeValue, value: typeValue };
                  })
                  .filter(Boolean);

                if (mappedTypes.length > 0) {
                  return mappedTypes;
                }
              }

              if (
                technicianData?.technician_types &&
                Array.isArray(technicianData.technician_types)
              ) {
                const mappedTypes = technicianData.technician_types
                  .map((techType) => {
                    const typeValue =
                      typeof techType === "object" ? techType?.type : techType;
                    if (!typeValue) return null;

                    const role = allTypeListArray.find(
                      (role) => role.value === typeValue
                    );
                    return role || { label: typeValue, value: typeValue };
                  })
                  .filter(Boolean);

                if (mappedTypes.length > 0) {
                  return mappedTypes;
                }
              }

              if (technicianData?.type) {
                const role = allTypeListArray.find(
                  (role) => role.value === technicianData.type
                );
                return role
                  ? [role]
                  : [
                      {
                        label: technicianData.type,
                        value: technicianData.type,
                      },
                    ];
              }

              return [];
            })()
          : [],
      pin: "",
      confirmPin: "",
    }),
    [technicianData, technicianDetails]
  );

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .trim()
      .min(3, "Name must be at least 3 characters")
      .required("Please enter name"),
    companyName: Yup.string()
      .trim()
      .min(3, "Company name must be at least 3 characters")
      .required("Please enter company name"),
    email: Yup.string()
      .trim()
      .min(3, "Email must be at least 3 characters")
      .required("Please enter email"),
    gender: Yup.string().required("Please select your gender"),
    phone: Yup.string()
      .required("Please enter mobile number")
      .min(8, "Mobile is too short - should be 8 chars minimum.")
      .max(15, "Mobile is too long - should be 15 chars maximum.")
      .matches(/^\d+$/, "Phone number must be numeric"),
    type: Yup.array()
      .min(1, "Please select at least one type")
      .required("Please select type"),
    country: Yup.object().required("Please select country"),
    pin: technicianData?.id
      ? Yup.string()
          .matches(/^\d{4}$/, "PIN must be a 4-digit number")
          .transform((value) => value?.padStart(4, "0"))
      : Yup.string()
          .required("Please Enter PIN")
          .matches(/^\d{4}$/, "PIN must be a 4-digit number")
          .transform((value) => value?.padStart(4, "0")),
    confirmPin: technicianData?.id
      ? Yup.string()
          .oneOf([Yup.ref("pin"), null], "PINs must match")
          .transform((value) => value?.padStart(4, "0"))
      : Yup.string()
          .required("Please enter confirm PIN")
          .oneOf([Yup.ref("pin"), null], "PINs must match")
          .transform((value) => value?.padStart(4, "0")),
    startTime: Yup.mixed().required("Please select a start time"),
    endTime: Yup.mixed().required("Please select an end time"),
  });

  const handleConfirmTechnicianData = async () => {
    setIsLoading(true);
    const payload = {
      full_name: formValues.fullName,
      email: formValues.email,
      company_name: formValues.companyName,
      gender: formValues.gender,
      type: formValues.type.map((t) => t.value),
      phone: formValues.phone,
      country_id: formValues.country.id,
      start_time: formValues.startTime
        ? dayjs(formValues.startTime).format("HH:mm:ss")
        : null,
      end_time: formValues.endTime
        ? dayjs(formValues.endTime).format("HH:mm:ss")
        : null,
    };

    if (technicianData?.id) {
      const pinString = String(formValues.pin);
      if (pinString.length > 0) {
        payload.pin = pinString;
        payload.confirm_pin = String(formValues.confirmPin);
      }
    } else {
      payload.pin = String(formValues.pin);
      payload.confirm_pin = String(formValues.confirmPin);
    }
    const action = technicianData?.id
      ? createTechnicians({ id: technicianData.id, ...payload })
      : createTechnicians(payload);

    try {
      const res = await dispatch(action);
      if (res.type === "createTechnicians/fulfilled") {
        return res;
      }
    } catch (error) {
      console.error("Error creating Technician", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validateInputs()) {
      return;
    }
    setIsLoading(true);
    const payload = {
      email: formValues?.email,
      password: password,
      confirmPassword: confirmPassword,
    };

    try {
      const response = await dispatch(newPassword(payload));
      if (response.type === "newPassword/fulfilled") {
        return response;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateInputs = () => {
    if (password.length) {
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return false;
      }
      if (password.length > 15) {
        setError("Password cannot be more than 15 characters.");
        return false;
      }

      const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/;
      if (!passwordRegex.test(password)) {
        setError(
          "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character."
        );
        return false;
      }

      if (password !== confirmPassword) {
        setError("Password and Confirm Password do not match.");
        return false;
      }
      return true;
    }
    return false;
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    let resetPasswordSuccess = false;
    let technicianDataSuccess = false;

    try {
      if (technicianData?.email_verified_at && !technicianData?.pin) {
        const resetPasswordResponse = await handleResetPassword();
        if (resetPasswordResponse) {
          resetPasswordSuccess = true;
        } else {
          resetPasswordSuccess = false;
        }
      } else {
        resetPasswordSuccess = true;
      }

      const technicianDataResponse = await handleConfirmTechnicianData();
      if (technicianDataResponse) {
        technicianDataSuccess = true;
      } else {
        technicianDataSuccess = false;
      }
    } catch (error) {
      resetPasswordSuccess = false;
      technicianDataSuccess = false;
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);

      if (password.length) {
        if (resetPasswordSuccess && technicianDataSuccess) {
          navigate("/technicians");
        }
      } else if (technicianDataSuccess) {
        navigate("/technicians");
      }
    }
  };

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/technicians");

  return (
    <>
      <div className="mb-5">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {isEditMode == "edit" ? "Edit Technician " : "Create Technician"}
        </h6>
        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={
            isEditMode === "edit"
              ? "Edit Technician Details"
              : "Create Technician Account"
          }
          breadCrumbParent2={"Technician"}
          breadCrumbTitleKey={"/"}
          classes=""
        />
      </div>
      <div className="w-full bg-bgWhite  shadow drop-shadow-md rounded-lg py-5 px-6">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setFormValues(values);
            setShowConfirmationModal(true);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-6 mb-4">
                {/* Name */}
                <InputWithLabel
                  className={`${
                    isEditMode === "edit" && !isEditing
                      ? "text-textGrey border border-dashed "
                      : "text-black  border hover:border-themeBtn"
                  }  ${
                    errors.fullName && touched.fullName
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  }  transition duration-300 ease-in-out`}
                  idFromName="fullName"
                  label="Name*"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.fullName}
                  errors={errors.fullName && touched.fullName}
                  errorsText={errors.fullName}
                  disabled={isEditMode == "edit" && !isEditing}
                />
                {/*Comopany Name  */}
                <InputWithLabel
                  className={`${
                    isEditMode === "edit" && !isEditing
                      ? "text-textGrey border border-dashed "
                      : "text-black  border hover:border-themeBtn"
                  }  ${
                    errors.companyName && touched.companyName
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  }  transition duration-300 ease-in-out`}
                  idFromName="companyName"
                  label="Company Name"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.companyName}
                  errors={errors.companyName && touched.companyName}
                  errorsText={errors.companyName}
                  disabled={isEditMode == "edit" && !isEditing}
                />
                {/* Gender */}
                <div>
                  <div className="flex items-center space-x-3">
                    <label
                      form="gender"
                      className="text-textSecondary text-base font-Proxima"
                    >
                      Gender
                    </label>
                    <div>
                      <label htmlFor="male" className="relative cursor-pointer">
                        <Input
                          type="radio"
                          id="male"
                          name="gender"
                          value="male"
                          className="sr-only peer"
                          checked={values.gender === "male"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        <div
                          className="px-4 py-1 border border-themeBtn text-themeBtn rounded-full 
                          hover:bg-disableThemeBtn hover:border-themeBtn hover:text-themeBtn peer-checked:bg-themeBtn peer-checked:text-white peer-checked:border-themeBtn"
                        >
                          Male
                        </div>
                      </label>
                    </div>
                    <div>
                      <label
                        htmlFor="female"
                        className="relative cursor-pointer"
                      >
                        <Input
                          type="radio"
                          id="female"
                          value="female"
                          name="gender"
                          className="sr-only peer"
                          checked={values.gender === "female"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        <div
                          className="px-4 py-1 border border-themeBtn text-themeBtn rounded-full 
                          hover:bg-disableThemeBtn hover:border-themeBtn hover:text-themeBtn peer-checked:bg-themeBtn peer-checked:text-white peer-checked:border-themeBtn"
                        >
                          Female
                        </div>
                      </label>
                    </div>
                    <div>
                      <label
                        htmlFor="other"
                        className="relative cursor-pointer"
                      >
                        <Input
                          type="radio"
                          id="other"
                          name="gender"
                          value="other"
                          className="sr-only peer"
                          checked={values.gender === "other"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={isEditMode == "edit" && !isEditing}
                        />
                        <div
                          className="px-4 py-1 border border-themeBtn text-themeBtn rounded-full 
                          hover:bg-disableThemeBtn hover:border-themeBtn hover:text-themeBtn peer-checked:bg-themeBtn peer-checked:text-white peer-checked:border-themeBtn"
                        >
                          Other
                        </div>
                      </label>
                    </div>
                  </div>
                  {errors.gender && touched.gender ? (
                    <>
                      <InputError errorTitle={errors.gender} />
                    </>
                  ) : null}
                </div>
                {/* Type */}
                <SelectWithLabel
                  idFromName="type"
                  placeholder="Types"
                  options={allTypeListArray}
                  value={values?.type?.map((option) => option.value) || []}
                  setFieldValue={setFieldValue}
                  errors={errors.type}
                  touched={touched.type}
                  multiple
                  disabled={isEditMode == "edit" && !isEditing}
                  endAdornment={
                    Array.isArray(values?.type) && values?.type.length > 0 ? (
                      <IconButton
                        sx={{
                          position: "relative",
                          right: "10px",
                        }}
                        onClick={() => setFieldValue("type", [])}
                      >
                        <Cancel sx={{ fontSize: "16px" }} />
                      </IconButton>
                    ) : null
                  }
                  onChange={(event) => {
                    const selectedValues = event.target.value;
                    const selectedOptions = allTypeListArray.filter((option) =>
                      selectedValues.includes(option.value)
                    );
                    setFieldValue("type", selectedOptions);
                  }}
                  renderValue={(selected) => (
                    <>
                      {selected.map((value) => {
                        const selectedOption = allTypeListArray?.find(
                          (option) => option.value === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={selectedOption?.label}
                            sx={{ marginRight: "5px" }}
                            clickable
                            variant="outlined"
                            deleteIcon={
                              <Cancel
                                onMouseDown={(event) => event.stopPropagation()}
                              />
                            }
                            onDelete={(e) => {
                              e.stopPropagation();
                              const newSelected = values.type.filter(
                                (t) => t.value !== value
                              );
                              setFieldValue("type", newSelected);
                            }}
                          />
                        );
                      })}
                    </>
                  )}
                />

                <div className="grid grid-cols-3 gap-3">
                  {/* Country */}
                  <div>
                    <div
                      className={`${
                        errors.country && touched.country
                          ? ` border border-red `
                          : "  border-borderTheme/80"
                      } relative bg-white rounded focus-within:border-themeBtn  hover:border-themeBtn transition duration-300 ease-in-out`}
                    >
                      <SelectBox
                        id="country"
                        placeholder="Country"
                        options={countryArray}
                        isLoading={countryLoading}
                        getOptionValue={(countryArray) => countryArray.id}
                        value={
                          countryArray?.length > 0 &&
                          countryArray?.find((op) => {
                            return op.id === values?.country?.id;
                          })
                        }
                        onChange={(e) => {
                          setFieldValue("country", e);
                        }}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                      <label
                        htmlFor="country"
                        className="absolute text-textGrey pointer-events-none text-base bg-white duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] px-2 peer-focus:px-2 peer-focus peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                      >
                        Country
                      </label>
                    </div>
                    {errors.country && touched.country ? (
                      <InputError errorTitle={errors.country} />
                    ) : null}
                  </div>
                  {/* Phone */}
                  <div className="col-span-2">
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black  border hover:border-themeBtn"
                      }  ${
                        errors.phone && touched.phone
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="phone"
                      label="Mobile"
                      type={"number"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      errors={errors.phone && touched.phone}
                      errorsText={errors.phone}
                      disabled={isEditMode == "edit" && !isEditing}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                </div>
                {/* Email */}
                <InputWithLabel
                  className={`${
                    isEditMode === "edit" && !isEditing
                      ? "text-textGrey border border-dashed "
                      : "text-black  border hover:border-themeBtn"
                  }  ${
                    errors.email && touched.email
                      ? `border border-red`
                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                  }  transition duration-300 ease-in-out`}
                  idFromName="email"
                  label="Email"
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  errors={errors.email && touched.email}
                  errorsText={errors.email}
                  disabled={isEditMode == "edit" && !isEditing}
                />
                {/* Start Time */}
                <div>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={3}>
                      <MobileTimePicker
                        label="Start Time"
                        value={values.startTime}
                        onChange={(newValue) => {
                          setFieldValue("startTime", dayjs(newValue));
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              padding: "0px",
                              minHeight: "unset",
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                  borderColor: "#F15B29",
                                  borderWidth: "1px",
                                },
                              },
                            },
                          },
                        }}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                      {errors.startTime && touched.startTime && (
                        <InputError errorTitle={errors.startTime} />
                      )}
                    </Stack>
                  </LocalizationProvider>
                </div>
                {/* End Time */}
                <div style={{ zIndex: 0 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={3}>
                      <MobileTimePicker
                        label="End Time"
                        value={values.endTime}
                        onChange={(newValue) =>
                          setFieldValue("endTime", dayjs(newValue))
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              padding: "0px",
                              minHeight: "unset",
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                  borderColor: "#F15B29",
                                  borderWidth: "1px",
                                },
                              },
                            },
                          },
                        }}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                      {errors.endTime && touched.endTime && (
                        <InputError errorTitle={errors.endTime} />
                      )}
                    </Stack>
                  </LocalizationProvider>
                </div>

                {technicianData?.email_verified_at && !technicianData?.pin ? (
                  <>
                    {/* Password Input */}
                    <div className="relative">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        name="password"
                        placeholder="Password*"
                        className={`border bg-white font-Proxima ${
                          errors.pin && touched.pin
                            ? "border-red"
                            : "border-borderTheme/80"
                        } px-4 py-2 rounded w-full`}
                        onChange={(e) => {
                          setFormValues({
                            id: formValues?.id,
                            password: e.target.value,
                            confirmPassword: formValues?.confirmPassword,
                          });
                          setPassword(e.target.value);
                        }}
                        value={password}
                        autoComplete="new-password"
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                      <span
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                      >
                        {passwordVisible ? <EyeSlash /> : <Eye />}{" "}
                      </span>
                    </div>
                    {/* Confirm Password Input */}
                    <div className="relative">
                      <input
                        type={confirmPasswordVisible ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Confirm New Password*"
                        className={`border font-Proxima ${
                          errors.pin && touched.pin
                            ? "border-red"
                            : "border-borderTheme/80"
                        } px-4 py-2 rounded w-full`}
                        onChange={(e) => {
                          setFormValues({
                            id: formValues?.id,
                            password: formValues?.password,
                            confirmPassword: e.target.value,
                          });
                          setConfirmPassword(e.target.value);
                        }}
                        value={confirmPassword}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                      <span
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer"
                        onClick={() =>
                          setConfirmPasswordVisible(!confirmPasswordVisible)
                        }
                      >
                        {confirmPasswordVisible ? <EyeSlash /> : <Eye />}{" "}
                      </span>
                    </div>
                    {error && <InputError errorTitle={error} />}
                  </>
                ) : (
                  <>
                    <div>
                      <InputWithLabel
                        className={`${
                          isEditMode === "edit" && !isEditing
                            ? "text-textGrey border border-dashed"
                            : "text-black border hover:border-themeBtn"
                        } ${
                          errors.pin && touched.pin
                            ? "border border-red"
                            : "border border-borderTheme/80 focus-within:border-themeBtn"
                        } transition duration-300 ease-in-out`}
                        idFromName="pin"
                        label="4-digit Pin*"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.pin}
                        errors={errors.pin && touched.pin}
                        errorsText={errors.pin}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <InputWithLabel
                        className={`${
                          isEditMode === "edit" && !isEditing
                            ? "text-textGrey border border-dashed"
                            : "text-black border hover:border-themeBtn"
                        } ${
                          errors.confirmPin && touched.confirmPin
                            ? "border border-red"
                            : "border border-borderTheme/80 focus-within:border-themeBtn"
                        } transition duration-300 ease-in-out`}
                        idFromName="confirmPin"
                        label="Confirm 4-digit Pin*"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.confirmPin}
                        errors={errors.confirmPin && touched.confirmPin}
                        errorsText={errors.confirmPin}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                    </div>
                  </>
                )}
              </div>
              {/* Submit button */}
              <div className="flex justify-end gap-3 mt-3">
                <DefaultPrimaryButton
                  type="button"
                  value={isEditMode === "edit" ? "Back" : "Cancel"}
                  onClick={handleCancelClick}
                />
                {isEditMode === "edit" ? (
                  isEditing ? (
                    <>
                      <DefaultButton
                        type="submit"
                        value="Save"
                        onClick={(e) => {
                          if (
                            technicianData?.email_verified_at &&
                            !technicianData?.pin &&
                            password?.length
                          ) {
                            if (!validateInputs()) {
                              e.preventDefault();
                              return;
                            }
                          }
                          return;
                        }}
                      />
                    </>
                  ) : (
                    <DefaultButton
                      value="Edit"
                      onClick={handleEditClick}
                      type="submit"
                    />
                  )
                ) : (
                  <>
                    <DefaultButton type="submit" value="confirm" />
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm()}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default CreateEditTechnicianAccount;
